import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Edit, Trash2, CheckCircle, XCircle, Download, Plus } from 'lucide-react';
import { customersApi } from '../../lib/api';
import PageHeader from '../../components/PageHeader';
import DataTable from '../../components/DataTable';
import Modal from '../../components/Modal';

interface Customer {
  id: string;
  customerCode: string;
  name: string;
  honorific: string;
  contactPerson: string;
  phone: string;
  fax: string;
  address: string;
  isActive: boolean;
}

export default function CustomerList() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [formData, setFormData] = useState({
    customerCode: '',
    name: '',
    honorific: '御中',
    contactPerson: '',
    phone: '',
    fax: '',
    postalCode: '',
    address: '',
    remarks: '',
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['customers', search],
    queryFn: () => customersApi.getAll({ search }),
  });

  const createMutation = useMutation({
    mutationFn: customersApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      toast.success('売り先を登録しました');
      setIsModalOpen(false);
      resetForm();
    },
    onError: () => toast.error('登録に失敗しました'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => customersApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      toast.success('売り先を更新しました');
      setIsModalOpen(false);
      resetForm();
    },
    onError: () => toast.error('更新に失敗しました'),
  });

  const deleteMutation = useMutation({
    mutationFn: customersApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      toast.success('売り先を削除しました');
    },
    onError: () => toast.error('削除に失敗しました'),
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      await Promise.all(ids.map(id => customersApi.delete(id)));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      toast.success('選択した売り先を削除しました');
    },
    onError: () => toast.error('一括削除に失敗しました'),
  });

  const bulkStatusMutation = useMutation({
    mutationFn: async ({ ids, isActive }: { ids: string[]; isActive: boolean }) => {
      await Promise.all(ids.map(id => customersApi.update(id, { isActive })));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      toast.success('ステータスを更新しました');
    },
    onError: () => toast.error('ステータス更新に失敗しました'),
  });

  const resetForm = () => {
    setFormData({
      customerCode: '',
      name: '',
      honorific: '御中',
      contactPerson: '',
      phone: '',
      fax: '',
      postalCode: '',
      address: '',
      remarks: '',
    });
    setEditingCustomer(null);
  };

  const handleEdit = (customer: Customer) => {
    setEditingCustomer(customer);
    setFormData({
      customerCode: customer.customerCode || '',
      name: customer.name,
      honorific: customer.honorific || '御中',
      contactPerson: customer.contactPerson || '',
      phone: customer.phone || '',
      fax: customer.fax || '',
      postalCode: '',
      address: customer.address || '',
      remarks: '',
    });
    setIsModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingCustomer) {
      updateMutation.mutate({ id: editingCustomer.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = (id: string) => {
    if (confirm('この売り先を削除しますか？')) {
      deleteMutation.mutate(id);
    }
  };

  const handleBulkDelete = (ids: string[]) => {
    if (confirm(`${ids.length}件の売り先を削除しますか？`)) {
      bulkDeleteMutation.mutate(ids);
    }
  };

  const handleBulkActivate = (ids: string[]) => {
    bulkStatusMutation.mutate({ ids, isActive: true });
  };

  const handleBulkDeactivate = (ids: string[]) => {
    bulkStatusMutation.mutate({ ids, isActive: false });
  };

  const handleExport = () => {
    const items = data?.data?.data || [];
    if (items.length === 0) {
      toast.error('エクスポートするデータがありません');
      return;
    }
    const headers = ['コード', '名前', '敬称', '住所', '電話番号', 'FAX', '状態'];
    const rows = items.map((item: Customer) => [
      item.customerCode || '',
      item.name || '',
      item.honorific || '',
      item.address || '',
      item.phone || '',
      item.fax || '',
      item.isActive ? '有効' : '無効',
    ]);
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `売り先マスタ_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('CSVをエクスポートしました');
  };

  const columns = [
    { key: 'customerCode', label: 'コード', width: '100px', sortable: true },
    { key: 'name', label: '売り先名', sortable: true },
    { key: 'honorific', label: '敬称', width: '80px' },
    { key: 'contactPerson', label: '担当者', width: '120px' },
    { key: 'phone', label: '電話番号', width: '140px' },
    { key: 'fax', label: 'FAX', width: '140px' },
    {
      key: 'isActive',
      label: '状態',
      width: '80px',
      sortable: true,
      render: (row: Customer) => (
        <span className={row.isActive ? 'badge badge-success' : 'badge badge-error'}>
          {row.isActive ? '有効' : '無効'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: '操作',
      width: '100px',
      render: (row: Customer) => (
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); handleEdit(row); }}
            className="p-1.5 rounded-lg transition-colors hover:bg-gray-100"
            style={{ color: 'var(--accent-color)' }}
            title="編集"
          >
            <Edit size={16} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleDelete(row.id); }}
            className="p-1.5 rounded-lg transition-colors hover:bg-gray-100"
            style={{ color: 'var(--error-color)' }}
            title="削除"
          >
            <Trash2 size={16} />
          </button>
        </div>
      ),
    },
  ];

  const bulkActions = [
    {
      label: '有効化',
      icon: <CheckCircle size={14} />,
      onClick: handleBulkActivate,
      variant: 'primary' as const,
    },
    {
      label: '無効化',
      icon: <XCircle size={14} />,
      onClick: handleBulkDeactivate,
      variant: 'outline' as const,
    },
    {
      label: '一括削除',
      icon: <Trash2 size={14} />,
      onClick: handleBulkDelete,
      variant: 'danger' as const,
    },
  ];

  return (
    <div className="animate-fadeIn">
      <PageHeader
        title="売り先マスタ"
        breadcrumbs={[{ label: 'マスタ管理' }]}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="売り先名で検索..."
        onApply={() => refetch()}
        actions={
          <div className="flex items-center gap-1.5">
            <button className="btn btn-outline btn-sm" onClick={handleExport}>
              <Download size={14} />
            </button>
            <button className="btn btn-primary btn-sm" onClick={() => { resetForm(); setIsModalOpen(true); }}>
              <Plus size={14} />
              売り先登録
            </button>
          </div>
        }
      />

      <DataTable
        columns={columns}
        data={data?.data?.data || []}
        loading={isLoading}
        selectable
        bulkActions={bulkActions}
        getRowId={(row: Customer) => row.id}
        emptyMessage="売り先が登録されていません"
        defaultSortKey="name"
        defaultSortOrder="asc"
      />

      <Modal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); resetForm(); }}
        title={editingCustomer ? '売り先編集' : '売り先登録'}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">売り先コード</label>
              <input
                type="text"
                className="input-field"
                value={formData.customerCode}
                onChange={(e) => setFormData({ ...formData, customerCode: e.target.value })}
              />
            </div>
            <div>
              <label className="label">敬称</label>
              <select
                className="input-field"
                value={formData.honorific}
                onChange={(e) => setFormData({ ...formData, honorific: e.target.value })}
              >
                <option value="御中">御中</option>
                <option value="様">様</option>
              </select>
            </div>
          </div>

          <div>
            <label className="label">売り先名 <span className="text-red-500">*</span></label>
            <input
              type="text"
              className="input-field"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">担当者名</label>
              <input
                type="text"
                className="input-field"
                value={formData.contactPerson}
                onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
              />
            </div>
            <div>
              <label className="label">郵便番号</label>
              <input
                type="text"
                className="input-field"
                value={formData.postalCode}
                onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
              />
            </div>
          </div>

          <div>
            <label className="label">住所</label>
            <input
              type="text"
              className="input-field"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">電話番号</label>
              <input
                type="text"
                className="input-field"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>
            <div>
              <label className="label">FAX番号</label>
              <input
                type="text"
                className="input-field"
                value={formData.fax}
                onChange={(e) => setFormData({ ...formData, fax: e.target.value })}
              />
            </div>
          </div>

          <div>
            <label className="label">備考</label>
            <textarea
              className="input-field"
              rows={3}
              value={formData.remarks}
              onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => { setIsModalOpen(false); resetForm(); }}
            >
              キャンセル
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {editingCustomer ? '更新' : '登録'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
